import { calcSeriespillStandings, serieMatchCourt } from '../../engine';
import { useT } from '../../i18n';
import { isErasedPlayer } from '../../lib/playerName';

export interface SeriePadBannerProps {
  /** The seriespill (league) tournament — matches/pairs/config drive the standings table. */
  series: any;
  /** The court whose matches scope the division shown. */
  court: any;
  /** All players (for last-name lookup in pair labels). */
  allPlayers?: any[];
  /** Optional match to focus the table on (its division + highlighted pairs). */
  focusMatch?: any;
  /**
   * Standings computed elsewhere, in `calcSeriespillStandings` order and shape.
   *
   * A pad fed by `/pad/state` gets ONLY ITS OWN COURT's matches, and a division
   * table built from those has different values and a different order (#328) —
   * so the server computes the table and the pad renders it. Omitted → computed
   * here from `series`, which is what every other caller does.
   */
  standings?: any[] | null;
}

/**
 * Condensed division standings banner pinned inside a seriespill court pad — picks the
 * focused match's division, computes its standings, and renders a compact table showing the
 * leader, the focused pairs and one neighbour above/below each (gaps collapsed to «···»).
 * Extracted verbatim from the original artifact (legacy/App.jsx); markup/logic unchanged.
 */
export function SeriePadBanner({
  series,
  court,
  allPlayers = [],
  focusMatch = null,
  standings: standingsProp = null,
}: SeriePadBannerProps) {
  const t = useT();
  const courtMatches = (series?.matches || []).filter(
    (m) => Number(serieMatchCourt(m)) === Number(court),
  );
  if (courtMatches.length === 0) return null;
  const focus = focusMatch || courtMatches.find((m) => !m.score) || courtMatches[0];
  const divId = focus?.divisionId;
  if (!divId) return null;
  let divName = t('seriespill.pad.division'),
    className = '';
  (series?.config?.classes || []).forEach((cls) =>
    (cls.divisions || []).forEach((d) => {
      if (d.id === divId) {
        divName = d.name || divName;
        className = cls.name || '';
      }
    }),
  );
  const divPairs = (series?.pairs || []).filter((p) => p.divisionId === divId);
  const divMatches = (series?.matches || []).filter((m) => m.divisionId === divId);
  let standings = [];
  if (standingsProp) {
    // Already computed — see the prop's comment. Not recomputed as a
    // cross-check either: the inputs for that are exactly what a pad must not
    // have, so "verify it locally" is not available by construction.
    standings = standingsProp;
  } else {
    try {
      standings = calcSeriespillStandings(divPairs, divMatches) || [];
    } catch (_e) {
      standings = [];
    }
  }
  const nameOf = (pid) => {
    const p = (allPlayers || []).find((x) => x.id === pid);
    if (!p) return '—';
    // The whole label for an erased player (#1321): its last word, «spiller»,
    // would read as a surname.
    if (isErasedPlayer(p)) return t('players.erased');
    const parts = (p.name || '').trim().split(' ');
    return parts[parts.length - 1] || p.name;
  };
  const pairLabel = (pair) => (pair ? `${nameOf(pair.p1Id)}/${nameOf(pair.p2Id)}` : '—');
  const focusPairIds = new Set([focus?.team1?.pairId, focus?.team2?.pairId].filter(Boolean));
  // Condensed table: show only the leader, the focused pairs and one neighbour above/below
  // each of them. Gaps between the positions are marked with «···». This keeps the table
  // compact and leaves room for the match.
  const focusIdxs = standings
    .map((st, i) => ({ st, i }))
    .filter((x) => focusPairIds.has(x.st.pair?.id))
    .map((x) => x.i);
  // deviation: type-only annotation (monolith is @ts-nocheck) — `new Set()` infers
  // Set<unknown>, so `[...showSet]` would be unknown[] and break the numeric index math
  // below. Typing it Set<number> is purely compile-time; the runtime values are unchanged.
  const showSet = new Set<number>();
  if (standings.length > 0) showSet.add(0); // always the leader
  if (focusIdxs.length > 0) {
    focusIdxs.forEach((fi) => {
      showSet.add(fi - 1);
      showSet.add(fi);
      showSet.add(fi + 1);
    });
  } else {
    // no focused match → show only the top 3
    [0, 1, 2].forEach((i) => showSet.add(i));
  }
  const shown = [...showSet].filter((i) => i >= 0 && i < standings.length).sort((a, b) => a - b);
  const rows = [];
  let prev = -1;
  shown.forEach((i) => {
    if (prev >= 0 && i > prev + 1) rows.push({ gap: true, key: 'g' + i });
    rows.push({ st: standings[i], key: standings[i].pair?.id });
    prev = i;
  });
  const Row = ({ st }) => {
    const hl = focusPairIds.has(st.pair?.id);
    return (
      <div
        className="grid items-center"
        style={{
          gridTemplateColumns: '16px 1fr auto auto',
          gap: 6,
          padding: '1.5px 7px',
          borderRadius: 6,
          background: hl ? 'rgba(20,184,166,0.20)' : 'transparent',
          border: hl ? '1px solid rgba(20,184,166,0.45)' : '1px solid transparent',
        }}
      >
        <span
          className="font-mono tabular-nums"
          style={{
            fontSize: 10,
            color: hl ? '#5eead4' : 'rgba(230,251,255,0.5)',
            fontWeight: hl ? 700 : 400,
          }}
        >
          {st.pos}
        </span>
        <span
          className="font-display truncate"
          style={{
            fontSize: 11,
            color: hl ? '#fff' : 'rgba(230,251,255,0.82)',
            fontWeight: hl ? 700 : 500,
          }}
        >
          {pairLabel(st.pair)}
        </span>
        <span
          className="font-mono tabular-nums flex-shrink-0"
          style={{ fontSize: 9, color: 'rgba(230,251,255,0.38)' }}
        >
          {t('seriespill.pad.playedShort', { count: st.played })}
        </span>
        <span
          className="font-mono font-bold tabular-nums flex-shrink-0"
          style={{
            fontSize: 12,
            color: hl ? '#5eead4' : '#e6fbff',
            minWidth: 18,
            textAlign: 'right',
          }}
        >
          {st.points}
        </span>
      </div>
    );
  };
  return (
    <div
      className="flex-shrink-0"
      style={{
        background: 'linear-gradient(180deg, rgba(20,184,166,0.12), rgba(8,9,13,0.55))',
        borderTop: '1px solid rgba(20,184,166,0.30)',
      }}
    >
      {/* Tydelig Klasse + Divisjon */}
      <div className="flex items-center justify-between" style={{ padding: '6px 12px 4px' }}>
        <div className="min-w-0 flex items-baseline gap-2">
          <span
            className="font-mono uppercase tracking-[0.16em] flex-shrink-0"
            style={{ fontSize: 9, color: '#5eead4' }}
          >
            {className || t('seriespill.pad.class')}
          </span>
          <span
            className="font-display font-bold truncate"
            style={{ color: '#fff', fontSize: 13, lineHeight: 1.05 }}
          >
            {divName}
          </span>
        </div>
        <span
          className="font-mono uppercase tracking-wider flex-shrink-0"
          style={{ fontSize: 9, color: 'rgba(230,251,255,0.4)' }}
        >
          {t('seriespill.pad.table')}
        </span>
      </div>
      <div style={{ padding: '0 9px 6px' }}>
        {standings.length > 0 ? (
          <div className="flex flex-col" style={{ gap: 1 }}>
            {rows.map((r) =>
              r.gap ? (
                <div
                  key={r.key}
                  className="text-center font-mono"
                  style={{ fontSize: 9, color: 'rgba(230,251,255,0.28)', lineHeight: 0.9 }}
                >
                  ···
                </div>
              ) : (
                <Row key={r.key} st={r.st} />
              ),
            )}
          </div>
        ) : (
          <div
            className="font-mono px-2 py-1"
            style={{ fontSize: 10, color: 'rgba(230,251,255,0.4)' }}
          >
            {t('seriespill.pad.noResults')}
          </div>
        )}
      </div>
    </div>
  );
}
